import PropTypes from 'prop-types';
import ReactHtmlParser from 'react-html-parser';
import { Box, Typography } from '@mui/material';
import { sayBlockSx, sayTagSx, sayTextSx, variableHighlightSx } from '../styles';

/**
 * Decodes HTML entities in text (e.g., &lt;br&gt; -> <br>)
 * @param {string} text - The text with HTML entities
 * @returns {string} Text with entities decoded
 */
const decodeHtmlEntities = (text) => {
  if (!text || typeof document === 'undefined') return text;
  const textarea = document.createElement('textarea');
  textarea.innerHTML = text;
  return textarea.value;
};

/**
 * Replaces merge tag patterns in text with their resolved values
 * @param {string} text - The text to parse
 * @param {object} mergeTagData - Object containing merge tag values
 * @returns {string} Text with merge tags replaced
 */
const replaceMergeTags = (text, mergeTagData = {}) => {
  if (!text) return '';

  // First decode any HTML entities (e.g., &lt;br&gt; -> <br>)
  let result = decodeHtmlEntities(text);

  // Remove heading tags (h1-h6) - they add excessive margins
  // First remove headings that only contain <br> or whitespace (empty headings)
  result = result.replace(/<h[1-6][^>]*>\s*(<br\s*\/?>)?\s*<\/h[1-6]>/gi, '<br>');
  // Then convert remaining heading tags to simple line breaks
  result = result.replace(/<h[1-6][^>]*>/gi, '');
  result = result.replace(/<\/h[1-6]>/gi, '<br>');

  // Convert <p> tags to simple line breaks to avoid excessive spacing
  result = result.replace(/<p[^>]*>/gi, '');
  result = result.replace(/<\/p>/gi, '<br>');

  // Convert multiple newlines to single <br>
  result = result.replace(/\n{2,}/g, '<br>');
  result = result.replace(/\n/g, '<br>');

  // Collapse multiple consecutive <br> tags into a single one to reduce excessive spacing
  result = result.replace(/(<br\s*\/?>\s*){2,}/gi, '<br>');

  // Remove leading/trailing <br> tags
  result = result.replace(/^(<br\s*\/?>)+/gi, '');
  result = result.replace(/(<br\s*\/?>)+$/gi, '');

  // Replace $variable patterns
  result = result.replace(/\$([a-zA-Z0-9_]+)/g, (match, variable) => {
    const value = mergeTagData[variable];
    if (value) {
      return `<span class="merge-tag-highlight">${value}</span>`;
    }
    return `<span class="merge-tag-highlight">${match}</span>`;
  });

  // Replace {{variable}} patterns
  result = result.replace(/\{\{([^}]+)\}\}/g, (match, variable) => {
    const trimmedVar = variable.trim();
    const value = mergeTagData[trimmedVar];
    if (value) {
      return `<span class="merge-tag-highlight">${value}</span>`;
    }
    return `<span class="merge-tag-highlight">${trimmedVar}</span>`;
  });

  return result;
};

/**
 * Transform function for ReactHtmlParser to style merge tag highlights
 */
const transformNode = (node) => {
  if (node.type === 'tag' && node.name === 'span' && node.attribs?.class === 'merge-tag-highlight') {
    const content = node.children?.[0]?.data || '';
    return (
      <Box
        key={Math.random()}
        component="span"
        sx={variableHighlightSx}
      >
        {content}
      </Box>
    );
  }
  return undefined;
};

const SayBlock = ({
  text,
  tag = 'Say',
  variant = 'primary',
  children,
  mergeTagData = {},
  sx = {},
}) => {
  // Process text: replace merge tags and parse HTML
  const processedText = replaceMergeTags(text, mergeTagData);

  return (
    <Box sx={{ ...sayBlockSx(variant), ...sx }}>
      <Box sx={sayTagSx(variant)}>{tag}</Box>
      {text ? (
        <Typography
          component="div"
          sx={{
            ...sayTextSx,
            // Ensure font styling is applied and not inherited from parent
            '& *': {
              fontSize: 'inherit',
              lineHeight: 'inherit',
            },
            // Minimize spacing on block elements
            '& p, & div, & h1, & h2, & h3, & h4, & h5, & h6': {
              margin: 0,
              marginBottom: '0.25rem',
              fontSize: 'inherit',
              fontWeight: 'inherit',
              lineHeight: 'inherit',
            },
            '& p:last-child, & div:last-child, & h1:last-child, & h2:last-child, & h3:last-child, & h4:last-child, & h5:last-child, & h6:last-child': {
              marginBottom: 0,
            },
          }}
        >
          {ReactHtmlParser(processedText, { transform: transformNode })}
        </Typography>
      ) : (
        children
      )}
    </Box>
  );
};

SayBlock.propTypes = {
  text: PropTypes.string,
  tag: PropTypes.string,
  variant: PropTypes.oneOf(['primary', 'info']),
  children: PropTypes.node,
  mergeTagData: PropTypes.object,
  sx: PropTypes.object,
};

export default SayBlock;
